import { alpha, type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import { type RefObject, useEffect, useState } from 'react';

interface GalleryHeaderCompactProps {
  /** Element to observe; when it scrolls out of view the compact title fades in. */
  observeRef: RefObject<HTMLElement | null>;
  name: string;
  totalItems: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    opacity: 0,
    maxWidth: 0,
    marginRight: 0,
    transition: 'opacity 0.2s ease, max-width 0.3s ease, margin 0.3s ease',
  },
  visible: {
    opacity: 1,
    maxWidth: 340,
    marginRight: 4,
  },
  icon: {
    width: 22,
    height: 22,
    borderRadius: 4,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dot: {
    color: alpha(theme.palette.text.primary, 0.4),
    fontSize: 12,
  },
  count: {
    fontSize: 12,
    color: alpha(theme.palette.text.primary, 0.6),
  },
}));

// Derives a 1-3 letter badge from a gallery name (e.g. "IntegTest-Gallery" -> "IG")
function initials(name: string): string {
  const parts = name
    .split(/[\s_\-:]+/)
    .filter(Boolean)
    .slice(0, 3);
  const letters = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
  return letters || name.slice(0, 2).toUpperCase();
}

const GalleryHeaderCompact: React.FC<GalleryHeaderCompactProps> = ({ observeRef, name, totalItems }) => {
  const classes = useStyles();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = observeRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisible(entry.intersectionRatio < 0.3);
        });
      },
      { threshold: [0, 0.3, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [observeRef]);

  return (
    <div className={`${classes.root} ${visible ? classes.visible : ''}`} aria-hidden={!visible}>
      <div className={classes.icon}>{initials(name)}</div>
      <span className={classes.title} title={name}>
        {name}
      </span>
      <span className={classes.dot}>·</span>
      <span className={classes.count}>{totalItems} items</span>
    </div>
  );
};

export default GalleryHeaderCompact;
